/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Path → human label dictionary.
 *
 * Diff paths are model-internal: `alertNotificationMedia.reset.email` means nothing to a support
 * engineer under time pressure. Translating them is the highest-value part of this feature, not
 * polish, and this is the single place it happens.
 *
 * Nothing here may throw. An unmapped path degrades to a humanised version of the path itself —
 * worse than a curated label, never worse than a blank row. Improving a label here improves it
 * everywhere at once.
 */

import { CHECKBOX_LABELS, SETTING_LABELS } from './alertSettings'

/**
 * Labels keyed by dot path. A `*` segment matches any single segment, and `{0}`, `{1}` … in the
 * label are replaced by the matched segments in order.
 *
 * Exact paths win over wildcards; among wildcards the one with fewer `*` segments wins, so a
 * specific rule is never shadowed by a broad one.
 */
export const FIELD_LABELS: Record<string, string> = {
  // Common
  name: 'Name',
  description: 'Description',
  enabled: 'Enabled',
  isActive: 'Active',
  companyId: 'Company',
  createdAt: 'Created',
  updatedAt: 'Updated',
  deleted: 'Deleted',

  // Alert configuration. Its settings live under `userConfig.*` and are labelled from the
  // behaviour templates — see `alertSettings.ts`; only the envelope is listed here.
  alertName: 'Alert name',
  severity: 'Severity',
  alertBehaviorId: 'Alert type',
  ownerCompanyId: 'Owner company',
  serviceEnvironment: 'Service environment',
  dateModified: 'Modified',
  // A header flag on the configuration screen, not one of the behaviour's settings.
  useNameForNotifications: 'Use alert config name for notifications',
  verbose: 'Verbose logging',
  'includedEntityGroup.names': 'Monitoring point name matches',
  'includedEntityGroup.excludedNames': 'Monitoring point name exclusions',
  'includedEntityGroup.assetGroup': 'Assets the name match is scoped to',
  'alertNotificationMedia.*.email': 'Email notification — {0}',
  'alertNotificationMedia.*.sms': 'SMS notification — {0}',
  'alertNotificationMedia.*.push': 'Push notification — {0}',
  'alertNotificationMedia.*.webhook': 'Webhook notification — {0}',
  'alertNotificationMedia.*.*': '{1} notification — {0}',
  'recipients.*': 'Recipient',
  recipients: 'Recipients',
  threshold: 'Threshold',

  // Alert subscription
  recipientUserId: 'Subscriber',
  includedEntityGroup: 'Included assets',
  'includedEntityGroup.*': 'Included assets — {0}',
  excludedEntityGroup: 'Excluded assets',
  'excludedEntityGroup.*': 'Excluded assets — {0}',

  // User administration. bonito writes kebab-case keys for a flat user record; `default-units` is
  // the only nested one. The indexed paths under `privilege-options` are deliberately not listed:
  // the renderer reassembles those into granted/revoked sets, and the generic "Privilege options ›
  // #2" fallback is more honest than a label implying the position means something.
  'first-name': 'First name',
  'last-name': 'Last name',
  'phone-number': 'Mobile',
  company: 'Company',
  'timezone-info-id': 'Timezone',
  'default-units': 'Measurement units',
  'default-units.distance': 'Distance units',
  'default-units.temperature': 'Temperature units',
  'user-profile': 'User profile',
  // Only surfaces on its own when the profile was renamed rather than reassigned; a reassignment
  // folds the id and the name into one row.
  'user-profile-name': 'User profile name',
  'privilege-options': 'Privilege options',
  'status-screen-display-options': 'Status screen options',
  'default-application-type': 'Default application',
  'is-sso-user': 'SSO user',
  'is-registered': 'Registered',
  'password-updated': 'Password',

  thresholdValue: 'Threshold',
  clearingPeriod: 'Clearing period',
  delayMinutes: 'Delay (minutes)',
  schedule: 'Schedule',

  // Virtual setpoint
  setPoint: 'Set point',
  minTemperature: 'Minimum temperature',
  maxTemperature: 'Maximum temperature',
  temperatureZone: 'Temperature zone',
  'zones.*.setPoint': 'Set point',
  'zones.*.minTemperature': 'Minimum temperature',
  'zones.*.maxTemperature': 'Maximum temperature',

  // Geofence
  category: 'Category',
  geofenceName: 'Name',
  visible: 'Visibility',
  polygon: 'Boundary',
  'polygon.*': 'Boundary point {0}',
  'coordinates.*': 'Boundary point {0}',
  radius: 'Radius',
  latitude: 'Latitude',
  longitude: 'Longitude',
  zOrder: 'Display layout',
  zorder: 'Display layout',
  // refresh keys categories by id, so the segment is an id, not a position.
  geofencecategories: 'Categories',
  'geofencecategories.*': 'Category',

  // Asset
  assetName: 'Asset name',
  assetType: 'Asset type',
  registration: 'Registration',
  make: 'Make',
  model: 'Model',
  vin: 'VIN',
  fridgeType: 'Fridge type',
  umdId: 'Monitoring device',
}

/** `alertNotificationMedia` → `Alert notification media`. */
export const humaniseSegment = (segment: string): string => {
  const words = segment
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()

  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase()
}

/**
 * Humanise a whole path, joining segments with `›` so nesting stays visible. Array indices render
 * as positions rather than raw numbers.
 */
export const humanisePath = (path: string): string => {
  if (!path) return 'Record'

  return path
    .split('.')
    .map((segment) => (/^\d+$/.test(segment) ? `#${String(Number(segment) + 1)}` : humaniseSegment(segment)))
    .join(' › ')
}

/** Match a path against a wildcard pattern, returning what the `*`s captured, or null. */
const matchPattern = (path: string[], pattern: string[]): string[] | null => {
  if (path.length !== pattern.length) return null

  const captures: string[] = []
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '*') captures.push(path[i])
    else if (pattern[i] !== path[i]) return null
  }

  return captures
}

// Fewest wildcards first, so `alertNotificationMedia.*.email` beats `alertNotificationMedia.*.*`.
const wildcardPatterns = Object.keys(FIELD_LABELS)
  .filter((pattern) => pattern.includes('*'))
  .sort((a, b) => a.split('*').length - b.split('*').length || a.length - b.length)

/** Where an alert configuration keeps its settings, and its per-setting "is it on" checkboxes. */
const USER_CONFIG = 'userConfig'
const OPTIONAL_SETTINGS = 'misc.optionalSettingsEnabled.'

/**
 * The label for an alert configuration setting.
 *
 * These are not listed in `FIELD_LABELS` because there are ~48 of them and they belong to a table
 * transcribed from the behaviour templates. An unlisted key still reads sensibly: the key itself is
 * humanised, so a setting added to a template after this build ships shows as "Some new setting"
 * rather than "User config › Some new setting".
 */
const alertSettingLabel = (segments: string[]): string | undefined => {
  if (segments[0] !== USER_CONFIG || segments.length < 2) return undefined

  const key = segments[1]
  const label = SETTING_LABELS[key] ?? CHECKBOX_LABELS[key] ?? humaniseSegment(key)

  // A list setting edited one slot at a time, e.g. `userConfig.inAnyGeofence.2`.
  if (segments.length === 3 && /^\d+$/.test(segments[2])) {
    return `${label} — #${String(Number(segments[2]) + 1)}`
  }

  return segments.length === 2 ? label : `${label} › ${segments.slice(2).map(humaniseSegment).join(' › ')}`
}

/** The human label for a diff path. Never throws, never returns an empty string. */
export const labelForPath = (path: string): string => {
  if (!path) return 'Record'

  const exact = FIELD_LABELS[path]
  if (exact) return exact

  const segments = path.split('.')

  const setting = alertSettingLabel(segments)
  if (setting) return setting

  // The checkbox that turns an optional setting on. It is normally folded into the setting it
  // mirrors; this labels the case where it changed on its own.
  if (path.startsWith(OPTIONAL_SETTINGS)) {
    const key = path.slice(OPTIONAL_SETTINGS.length)
    return `${SETTING_LABELS[key] ?? humaniseSegment(key)} — setting enabled`
  }

  for (const pattern of wildcardPatterns) {
    const captures = matchPattern(segments, pattern.split('.'))
    if (!captures) continue

    return FIELD_LABELS[pattern].replace(/\{(\d+)\}/g, (placeholder, index: string) => {
      const position = Number(index)
      return position < captures.length ? humaniseSegment(captures[position]) : placeholder
    })
  }

  return humanisePath(path)
}
